import type {Dispatch, SetStateAction} from 'react';
// @ts-expect-error
import type {Control, UseFormGetValues, UseFormSetValue, Watch} from 'react-hook-form';

import {LoadingPage} from 'shells/loading-page/loading-page';

import type {FormPageFragment} from 'nutshell-graphql-types';

import type {FormSchemaProperty} from '../../../../core/nutshell-fields/types';

import type {MultiPageForm} from '../types';

import {AddCustomField} from './add-custom-field/add-custom-field';
import type {EditCustomFieldSession, NewCustomFieldFormPayload} from './add-custom-field/types';
import {BlockEditor} from './block-editor/block-editor';
import {BlocksAndDesignPanel} from './blocks-and-design/blocks-and-design-panel';
import type {CustomFieldView, FieldWithArrayKey} from './blocks-and-design/content-blocks/types';
import {BranchingRules} from './branching-rules/branching-rules';
import {EditCustomField} from './edit-custom-field/edit-custom-field';
import {ReorderPages} from './reorder-pages/reorder-pages';

export type SidebarState =
    | 'blocks-and-design'
    | 'branching-rules'
    | 'reorder-pages'
    | 'block-editor'
    | 'add-custom-field'
    | 'loading'
    | 'edit-custom-field';

interface Props {
    formId: string;
    sidebarState: SidebarState;
    setSidebarState: (sidebarState: SidebarState) => void;
    control: Control<MultiPageForm>;
    watch: Watch;
    getValues: UseFormGetValues<MultiPageForm>;
    customFieldView: CustomFieldView | null;
    formFields: FieldWithArrayKey[];
    editCustomFieldSession: EditCustomFieldSession | null;
    setEditCustomFieldSession: Dispatch<SetStateAction<EditCustomFieldSession | null>>;
    setValue: UseFormSetValue<MultiPageForm>;
    onPickExistingSchemaFieldForForm: (field: FormSchemaProperty) => void;
    graphqlPages?: FormPageFragment[] | null;
    activePageIndex: number | null | undefined;
    onSubmitNewField: (payload: NewCustomFieldFormPayload) => Promise<void>;
}

export function Sidebar(props: Props) {
    return (
        <div className='flex flex-dir-col full-height pb-32'>
            {props.sidebarState === 'blocks-and-design' && (
                <BlocksAndDesignPanel control={props.control} />
            )}
            {props.sidebarState === 'branching-rules' && (
                <BranchingRules
                    setSidebarState={props.setSidebarState}
                    graphqlPages={props.graphqlPages}
                    activePageIndex={props.activePageIndex}
                />
            )}
            {props.sidebarState === 'reorder-pages' && <ReorderPages />}
            {props.sidebarState === 'block-editor' && <BlockEditor />}
            {props.sidebarState === 'add-custom-field' && props.customFieldView && (
                <AddCustomField
                    view={props.customFieldView}
                    setSidebarState={props.setSidebarState}
                    onPickExistingSchemaField={props.onPickExistingSchemaFieldForForm}
                    onSubmitNewField={props.onSubmitNewField}
                    formFields={props.formFields}
                />
            )}
            {props.sidebarState === 'loading' && <LoadingPage padding={80} />}
            {props.sidebarState === 'edit-custom-field' && props.editCustomFieldSession && (
                <EditCustomField
                    key={`${props.editCustomFieldSession.editedFieldRef.pageIndex}-${props.editCustomFieldSession.editedFieldRef.fieldIndex}`}
                    formId={props.formId}
                    session={props.editCustomFieldSession}
                    control={props.control}
                    watch={props.watch}
                    getValues={props.getValues}
                    setValue={props.setValue}
                    onBack={() => {
                        props.setEditCustomFieldSession(null);
                        props.setSidebarState('blocks-and-design');
                    }}
                />
            )}
        </div>
    );
}
